import React from 'react';
import PropTypes from 'prop-types';
import { Box, Alert } from '@mui/material';
import { ReferenceManager, ReferenceActions } from '@platform/core/src/components/domain';
import { useDates } from '@platform/core/src/context/DateContext';
import WorkoutSessionReferenceViewer from './WorkoutSessionReferenceViewer';
import WorkoutPlanReferenceViewer from './WorkoutPlanReferenceViewer';
import BioLogReferenceViewer from './BioLogReferenceViewer'; 
import { isPast, endOfDay } from 'date-fns';

const WorkoutSmartDateDetails = ({ eventData }) => {
  const { loadDates } = useDates();

  if (!eventData?.id) {
    return <Alert severity="warning">Cannot load scheduled items without an event ID.</Alert>;
  }

  // Check for Workout Session
  const hasWorkoutSession = eventData.references?.some(
    ref => ref.child_type === 'workout_session' || ref.parent_type === 'workout_session'
  );

  if (hasWorkoutSession) {
    return (
      <Box sx={{ mt: 2 }}>
        <ReferenceManager
          title="Logged Workout"
          childIds={[eventData.id]}
          childResourceType={['date']}
          parentResourceType={['workout_session']}
          hideKnownChild
          actions={ReferenceActions.VIEW_ONLY}
          parentViewer={WorkoutSessionReferenceViewer}
          onActionSuccess={loadDates}
        />
      </Box>
    );
  }

  // Check for Bio Log
  const hasBioLog = eventData.references?.some(
    ref => ref.child_type === 'bio_log' || ref.parent_type === 'bio_log'
  );

  if (hasBioLog) {
    return (
      <Box sx={{ mt: 2 }}>
        <ReferenceManager
          title="Health Log"
          childIds={[eventData.id]}
          childResourceType={['date']}
          parentResourceType={['bio_log']}
          hideKnownChild
          actions={ReferenceActions.VIEW_ONLY}
          parentViewer={BioLogReferenceViewer}
          onActionSuccess={loadDates}
        />
      </Box>
    );
  }

  // Check for Workout Plan
  const eventDate = new Date(eventData.start_datetime);
  const isDateInPast = isPast(endOfDay(eventDate));

  const isChildOfPlan = eventData.references?.some(ref => ref.parent_type === 'workout_plan');

  if (isChildOfPlan) {
    return (
      <Box sx={{ mt: 2 }}>
        <ReferenceManager
          title="Scheduled Plan"
          childIds={[eventData.id]}
          childResourceType={['date']}
          parentResourceType={['workout_plan']}
          hideKnownChild
          onActionSuccess={loadDates}
          parentViewer={WorkoutPlanReferenceViewer}
          actions={isDateInPast ? ReferenceActions.VIEW_ONLY : ReferenceActions.ALL}
        />
      </Box>
    );
  }

  const hasChildWorkoutPlan = eventData.references?.some(
    ref => ref.child_type === 'workout_plan'
  );

  if (isDateInPast && !hasChildWorkoutPlan) {
    return (<></>);
  }

  return (
    <Box sx={{ mt: 2 }}>
      <ReferenceManager
        title="Scheduled Plan"
        parentIds={[eventData.id]}
        parentResourceType={['date']}
        childResourceType={['workout_plan']}
        hideKnownParent
        onActionSuccess={loadDates}
        childViewer={WorkoutPlanReferenceViewer}
        actions={isDateInPast ? ReferenceActions.VIEW_ONLY : ReferenceActions.ALL}
      />
    </Box>
  );
};

WorkoutSmartDateDetails.propTypes = {
  eventData: PropTypes.object.isRequired,
};

export default WorkoutSmartDateDetails;